// El demo se recorre solo cuando nadie lo toca.
//
// Quien llega desde un anuncio no sabe que hay que elegir un curso, ni que
// después vienen tres «OK · Siguiente». El demo se quedaba en la primera
// pantalla del wizard y lo único que el visitante veía era un formulario vacío
// — o sea justo lo contrario de lo que esa pantalla tiene que probar. Esto lo
// empuja: si el visitante no hace nada, el demo avanza; si hace algo, manda él
// y el guión se corre a un lado y espera.
//
// Vive acá y no adentro de MaterialAiGenerate a propósito. Ese componente es el
// de producción: su única concesión al demo es entregarle la instancia a este
// guión (`arrancarAutoplayInyectado`). Todo lo que se aprieta se aprieta desde
// afuera, con los mismos métodos que movería un dedo, así que el modo demo no
// deja ninguna rama nueva en el camino del usuario que paga.

import { scrollToElementTop } from "@/utils/scrollToTop";

/**
 * Cuántos ejercicios tiene que mostrar el demo.
 *
 * Se cuenta en EJERCICIOS y no en OAs porque es lo que se ve: una hoja con dos
 * actividades no parece un material, parece una muestra. El guión marca los OAs
 * que hagan falta para llegar a este número.
 *
 * Techo real: lo que la captura tenga. Un OA con un solo ejercicio capturado no
 * puede dar dos —devolvería el mismo dos veces, o sea el generador mostrando que
 * no varía—, así que si la captura no alcanza, el demo muestra lo que hay. Para
 * subir el piso hay que capturar más:
 *
 *     python manage.py capturar_demo_materiales --grade … --subject … --oa 1,2,3,…
 */
const EJERCICIOS_OBJETIVO = 7;

const ESPERA = {
  // Lo que se le da al visitante para leer la pantalla y decidir. Más corto se
  // siente atropellado; más largo se lee como que el demo se colgó, que es
  // exactamente lo que hay que evitar.
  paso: 2600,
  // Dentro de un mismo paso (curso → asignatura, el segundo OA): es la
  // continuación de un gesto, no una decisión nueva.
  seleccion: 1400,
  // Del tercer gesto igual en adelante. Marcar siete OAs a 1,4 s cada uno son
  // diez segundos de ver aparecer tildes: a esa altura ya se entendió.
  repeticion: 700,
  // Del material terminado al mensaje de cierre. Largo a propósito: es el
  // momento en que el visitante mira lo que le están vendiendo.
  cierre: 7000,
  // Después de que el visitante toca algo, el guión le deja la pantalla.
  trasTocar: 9000,
};

/**
 * Los métodos del wizard que este guión aprieta.
 *
 * Se comprueban al arrancar, y ruidosamente. El demo anterior heredaba de
 * MaterialAiGenerate y sobrescribía métodos internos; cuando el componente se
 * reescribió, esos métodos dejaron de existir y los overrides quedaron sin
 * interceptar nada, sin que nada avisara. Un guión que llama a un método que ya
 * no existe falla igual de callado —el demo simplemente se queda quieto— y
 * quedarse quieto es el bug que este archivo vino a arreglar.
 */
const SUPERFICIE = [
  "onGradeSelect",
  "onSubjectSelect",
  "goToOas",
  "toggleOa",
  "goToIndicators",
  "advanceCounts",
  "bump",
  "bumpPeldano",
  "peldanoCount",
  "peldanoBloqueado",
];

/**
 * Reparte `total` entre `cajas` lo más parejo posible: (5, 4) → [2, 1, 1, 1].
 */
function repartir(total, cajas) {
  const salida = new Array(cajas).fill(0);
  for (let i = 0; i < total; i += 1) salida[i % cajas] += 1;
  return salida;
}

/** El primer gesto de un paso se mira; del tercero en adelante ya se entendió. */
function ritmo(hechos) {
  if (!hechos) return ESPERA.paso;
  return hechos === 1 ? ESPERA.seleccion : ESPERA.repeticion;
}

/**
 * Construye el guión que recorre el wizard del demo.
 *
 * @param {object}  [opciones]
 * @param {string}  [opciones.pasoDeCierre]  paso del chat al que saltar cuando
 *   el material queda listo. El wizard no lo sabe —ni tiene por qué—: emite
 *   `go-to-step` y el chat del demo decide.
 * @param {number}  [opciones.objetivo]  cuántos ejercicios mostrar.
 * @param {Object<string, number>} [opciones.disponiblesPorOa]  cuántos
 *   ejercicios distintos tiene capturados cada OA (`preguntasPorOa` del
 *   fixture). Sin esto se asume uno por OA, que es el caso conservador.
 * @returns {(comp: object) => () => void} función que recibe la instancia de
 *   MaterialAiGenerate y devuelve cómo detenerse.
 */
export function crearAutoplayMateriales(opciones = {}) {
  const objetivo = Number(opciones.objetivo) || EJERCICIOS_OBJETIVO;
  const disponiblesPorOa = opciones.disponiblesPorOa || {};
  const pasoDeCierre = opciones.pasoDeCierre || "closingMessage";

  /** Cuántos ejercicios distintos puede dar este OA. Sin dato, uno. */
  const disponibles = (oa) =>
    Math.max(1, Number(disponiblesPorOa[oa && oa.id]) || 1);

  return function autoplay(comp) {
    const faltantes = SUPERFICIE.filter((m) => typeof comp[m] !== "function");
    if (faltantes.length) {
      console.error(
        "[materialsDemoAutoplay] el wizard cambió de forma y el demo se va a " +
          "quedar quieto en la primera pantalla. Faltan:",
        faltantes
      );
      return () => {};
    }

    let vivo = true;
    let cerrado = false;
    let temporizador = null;
    // Hasta cuándo el visitante tiene la palabra. Se corre hacia adelante con
    // cada clic suyo, así que mientras esté usando el demo el guión no aparece.
    let pausadoHasta = 0;
    // OAs a los que ya se les fijó la cantidad. Se hace una sola vez por OA, y
    // apenas se llega al paso: si el visitante después sube el contador, sube.
    const cuentaFijada = new Set();

    const cancelar = () => {
      if (temporizador) clearTimeout(temporizador);
      temporizador = null;
    };

    // ── lo que el guión aprieta ──────────────────────────────────────────────
    //
    // Los `<select>` están atados con `:value` a un computed, así que fijar el
    // estado por el mismo handler del `@change` deja el control mostrando lo
    // elegido: el visitante ve el curso aparecer en la casilla, no un salto.

    const elegirCurso = () => {
      const grade = comp.grades[0];
      if (grade) comp.onGradeSelect({ target: { value: String(grade.code) } });
    };

    const elegirAsignatura = () => {
      const subject = comp.subjects[0];
      if (subject) {
        comp.onSubjectSelect({ target: { value: String(subject.code) } });
      }
    };

    const marcarSiguienteOa = () => {
      const siguiente = comp.oas.find((oa) => !oa.selected);
      if (siguiente) comp.toggleOa(siguiente);
    };

    /**
     * Lleva un contador a `meta` apretando el mismo −/+ que apretaría un dedo.
     *
     * Con tope y comprobando que el número se mueva: el componente puede negarse
     * (un peldaño bloqueado, el máximo por OA) y un `while` optimista se
     * volvería un cuelgue del navegador, no un demo lento.
     */
    const ajustar = (leer, mover, meta) => {
      for (let i = 0; i < 16; i += 1) {
        const antes = leer();
        if (antes === meta) return;
        mover(antes > meta ? -1 : 1);
        if (leer() === antes) return;
      }
    };

    /** Deja el OA con `cuantas` actividades, repartidas entre lo que se pueda. */
    const fijarCantidad = (oa, cuantas) => {
      if (!oa) return;
      if (!comp.usesPeldanosUi) {
        // Camino sin peldaños (Lenguaje, media): se reparte entre indicadores.
        const filas =
          oa.indicators && oa.indicators.length ? oa.indicators : [oa];
        const reparto = repartir(cuantas, filas.length);
        filas.forEach((fila, i) => {
          ajustar(
            () => Number(fila.count) || 0,
            (d) => comp.bump(fila, d),
            reparto[i]
          );
        });
        return;
      }
      const peldanos = [1, 2, 3, 4].filter((p) => !comp.peldanoBloqueado(oa, p));
      if (!peldanos.length) return;
      // Repartido y no todo en el primer peldaño: la escalera es parte de lo que
      // el demo tiene que mostrar — el mismo OA con y sin apoyo.
      const reparto = repartir(cuantas, peldanos.length);
      peldanos.forEach((p, i) => {
        ajustar(
          () => comp.peldanoCount(oa, p),
          (d) => comp.bumpPeldano(oa, p, d),
          reparto[i]
        );
      });
    };

    /**
     * Fija la cantidad del OA que se está mostrando, si todavía no se hizo.
     *
     * Va acá y no dentro de la acción con reloj porque también tiene que correr
     * cuando el que llegó al paso fue el visitante: el default del producto son
     * los cuatro peldaños, y la captura trae UN ejercicio por objetivo, así que
     * ese default hace que el demo muestre cuatro veces el mismo ejercicio. Es
     * un cambio de default, no una imposición: si el visitante sube el
     * contador, se queda como él lo dejó.
     */
    const fijarCuentaSiHaceFalta = () => {
      const oa = comp.currentCountOa;
      if (!oa) return;
      const llave = oa.id || oa.name;
      if (cuentaFijada.has(llave)) return;
      cuentaFijada.add(llave);
      // Lo que falta para el objetivo, repartido entre los OAs que todavía no
      // tienen cantidad — y nunca más de lo que este OA tiene capturado.
      const yaPedidos = comp.selectedOas.reduce(
        (suma, otro) =>
          cuentaFijada.has(otro.id || otro.name) && otro !== oa
            ? suma + comp.oaTotal(otro)
            : suma,
        0
      );
      const sinFijar = comp.selectedOas.filter(
        (otro) => !cuentaFijada.has(otro.id || otro.name)
      ).length;
      const falta = Math.max(0, objetivo - yaPedidos);
      const cuota = Math.ceil(falta / Math.max(1, sinFijar + 1));
      fijarCantidad(oa, Math.max(1, Math.min(disponibles(oa), cuota)));
    };

    const avanzarCuentas = () => {
      fijarCuentaSiHaceFalta();
      if (!comp.canAdvanceCounts) {
        console.warn(
          "[materialsDemoAutoplay] no pude dejar ninguna actividad en este OA; " +
            "el demo se detiene acá."
        );
        return;
      }
      comp.advanceCounts();
    };

    // El wizard avisa que terminó con `material-done`, que en el chat del demo
    // no escucha nadie: ChatMessageComponent sólo repite `go-to-step`. O sea el
    // chat se quedaba para siempre en el paso del wizard y el cierre —el CTA
    // por el que existe la pantalla— no llegaba nunca. Se emite desde acá para
    // no meterle al componente de producción un paso que sólo existe en el demo.
    const cerrar = () => {
      cerrado = true;
      comp.$emit("go-to-step", pasoDeCierre);
    };

    // ── qué toca ahora ───────────────────────────────────────────────────────
    //
    // Se deriva del estado, no de una lista de pasos: así da lo mismo si llegó
    // acá el visitante o el guión, y si el visitante retrocede con «Volver», el
    // guión lo acompaña en vez de seguir su propia película.

    const siguienteAccion = () => {
      if (comp.bootError) return null;
      switch (comp.step) {
        case "pick":
          if (comp.loadingGrades || comp.loadingSubjects || comp.loadingOas) {
            return null;
          }
          if (!comp.selectedGrade) {
            return { espera: ESPERA.paso, hacer: elegirCurso };
          }
          if (!comp.selectedSubject) {
            return { espera: ESPERA.seleccion, hacer: elegirAsignatura };
          }
          return { espera: ESPERA.seleccion, hacer: () => comp.goToOas() };
        case "oas": {
          if (comp.loadingOas || !comp.oas.length) return null;
          // Se marcan OAs hasta juntar los ejercicios del objetivo, no hasta un
          // número fijo de OAs: cuántos hacen falta depende de cuántos ejercicios
          // tenga capturado cada uno.
          const cubiertos = comp.selectedOas.reduce(
            (suma, oa) => suma + disponibles(oa),
            0
          );
          const quedan = comp.oas.some((oa) => !oa.selected);
          if (cubiertos < objetivo && quedan) {
            return { espera: ritmo(comp.selectedOas.length), hacer: marcarSiguienteOa };
          }
          return { espera: ESPERA.paso, hacer: () => comp.goToIndicators() };
        }
        case "indicators":
          if (comp.generating) return null;
          return { espera: ritmo(comp.currentOaIndex), hacer: avanzarCuentas };
        case "streaming":
          // El stream ya se anima solo y el feed sigue a la tarjeta nueva por
          // dentro. Acá no hay nada que empujar.
          return null;
        case "done":
          return cerrado ? null : { espera: ESPERA.cierre, hacer: cerrar };
        default:
          return null;
      }
    };

    const replanificar = () => {
      cancelar();
      if (!vivo) return;
      if (comp.step === "indicators" && !comp.generating) {
        fijarCuentaSiHaceFalta();
      }
      const accion = siguienteAccion();
      if (!accion) return;
      const espera = Math.max(accion.espera, pausadoHasta - Date.now());
      temporizador = setTimeout(() => {
        temporizador = null;
        if (!vivo) return;
        // Se vuelve a preguntar en vez de ejecutar lo que se decidió hace tres
        // segundos: en el medio el visitante pudo avanzar solo.
        const ahora = siguienteAccion();
        if (ahora) ahora.hacer();
      }, espera);
    };

    // Cualquier gesto del visitante dentro del wizard corre el guión hacia
    // adelante. En fase de captura y sin `preventDefault`: sólo se escucha. El
    // guión no dispara eventos del DOM —llama a los métodos— así que no se
    // despierta a sí mismo.
    const alTocar = () => {
      pausadoHasta = Date.now() + ESPERA.trasTocar;
      replanificar();
    };
    const raiz = comp.$el;
    if (raiz && raiz.addEventListener) {
      raiz.addEventListener("pointerdown", alTocar, true);
      raiz.addEventListener("change", alTocar, true);
    }

    // El wizard se monta chico —todavía sin cursos— y crece cuando llegan las
    // listas, así que el scroll que hizo el chat al aparecer la burbuja lo deja
    // abajo del pliegue. En un teléfono eso son dos segundos mirando nada. El
    // componente ya se reposiciona en cada cambio de paso; lo que faltaba era la
    // primera vez.
    if (raiz) scrollToElementTop(raiz);

    const dejarDeMirar = comp.$watch(
      () =>
        [
          comp.step,
          comp.bootError,
          comp.loadingGrades,
          comp.loadingSubjects,
          comp.loadingOas,
          comp.generating,
          comp.selectedGrade && comp.selectedGrade.code,
          comp.selectedSubject && comp.selectedSubject.code,
          comp.oas.length,
          comp.selectedOas.length,
          comp.currentOaIndex,
        ].join("|"),
      replanificar,
      { immediate: true }
    );

    return function detener() {
      vivo = false;
      cancelar();
      dejarDeMirar();
      if (raiz && raiz.removeEventListener) {
        raiz.removeEventListener("pointerdown", alTocar, true);
        raiz.removeEventListener("change", alTocar, true);
      }
    };
  };
}

export default crearAutoplayMateriales;
